import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { DeviationQrCodeDto } from '../../models/deviationQrCodeDto';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

/**
 * Minimal shape of the angularx-qrcode component instance we rely on.
 * A template ref on the <qrcode> element resolves to the component instance,
 * which exposes its rendered wrapper through `qrcElement`.
 */
interface QrCodeComponentRef {
  qrcElement: ElementRef<HTMLElement>;
}

@Component({
  selector: 'app-deviation-qr-generator',
  templateUrl: './deviation-qr-generator.component.html',
  styleUrls: ['./deviation-qr-generator.component.scss'],
  standalone: false
})
export class DeviationQrGeneratorComponent {
  @Input() visible: boolean = false;
  @Input() qrCodeData?: DeviationQrCodeDto = null;
  @Input() isLoading: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() createQrCodeEvent = new EventEmitter<string | null>();
  @Output() revokeQrCodeEvent = new EventEmitter<void>();

  @ViewChild('qrCode') qrCode?: QrCodeComponentRef;

  constructor(private translate: TranslateService) {}

  isRevoking: boolean = false;
  minDate: Date = new Date(); // Minimum date is today
  expirationDate?: Date = null;

  /**
   * Hide the modal and reset revocation state
   */
  onHide() {
    this.visible = false;
    this.isRevoking = false;
    this.visibleChange.emit(false);
  }

  /**
   * Emit the create event with the selected expiration date as a local yyyy-MM-dd
   * string (the format the backend's DateOnly expects), or null if not set.
   * Formatting in local time avoids the off-by-one day a UTC conversion would cause.
   */
  createQrCode() {
    const expiresOn = this.expirationDate
      ? formatDate(this.expirationDate, 'yyyy-MM-dd', 'en-US')
      : null;
    this.createQrCodeEvent.emit(expiresOn);
  }

  /**
   * Display the expiration date in a user-friendly format, or a localized "No expiration date" message if not set
   */
  displayExpirationDate(): string {
    if (!this.qrCodeData?.ExpiresOn) {
      return this.translate.instant('NO_EXPIRATION_DATE');
    }
    return formatDate(this.qrCodeData.ExpiresOn, 'dd.MM.yyyy', 'en-US');
  }

  /** Show the revoke confirmation view */
  onRevokeQrCode() {
    this.isRevoking = true;
  }

  /** Go back to the QR code view from the confirmation */
  onCancelRevoke() {
    this.isRevoking = false;
  }

  /** Confirm revocation and return to the empty view */
  onConfirmRevoke() {
    this.revokeQrCodeEvent.emit();
    this.qrCodeData = null;
    this.isRevoking = false;
  }

  /** Download the rendered QR code as a PNG image */
  onPrintQrCode() {
    const el = this.qrCode?.qrcElement?.nativeElement;
    if (!el) {
      return;
    }

    const canvas = el.querySelector('canvas');
    const img = el.querySelector('img');
    const dataUrl = canvas ? canvas.toDataURL('image/png') : (img?.src ?? null);

    if (!dataUrl) {
      return;
    }

    const fileName = this.translate.instant('DEVIATION_QR_CODE_FILE_NAME');

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName + '.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
